import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { appClient, authClient } from 'api/apiClient';
import { buildFileFormData, type StaffRoleEntry } from 'api/v1Helpers';
import { notify } from 'components/Common/Toast';
import { resetState } from 'redux/action/resetState';
import { UpdateMePayload, UserInfo, UserState } from 'types/user';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { normalizeParish, type ParishPublic } from 'types/parishes';

export interface ResponseOverRide extends AxiosResponse {
    user?: UserInfo | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    responseData?: any;
    code?: number;
    message?: string;
    statusCode?: string;
}

const initialState: UserState = {
    userInfo: null,
    loading: false,
    error: null,
    updateInfoLoading: false,
    updateInfoError: null,
    defaultMerchant: null,
    defaultCompanyId: null,
    userResponse: null,
    address: null,
    logoutLoading: false,
    selectedParishId: null,
    selectedParishName: null,
    staffRoles: [],
    staffRolesContractReady: false,
    mustChangePassword: false,
    myParishes: [],
    myParishesLoading: false,
    /** True after GET /v1/me settled (success or soft-fail). */
    meHydrated: false,
    /** True after GET /v1/me/parishes settled. */
    myParishesHydrated: false,
};

function normalizeStaffRoles(raw: unknown): { roles: StaffRoleEntry[]; contractReady: boolean } {
    if (!Array.isArray(raw)) {
        return { roles: [], contractReady: false };
    }
    const roles: StaffRoleEntry[] = [];
    for (const entry of raw) {
        if (!entry || typeof entry !== 'object') continue;
        const parishId = (entry as StaffRoleEntry).parishId;
        const role = (entry as StaffRoleEntry).role;
        if (typeof parishId !== 'string' || !parishId) continue;
        if (role !== 'CLERK' && role !== 'MERCHANT_ADMIN' && role !== 'PROVIDER_ADMIN') continue;
        roles.push({ parishId, role });
    }
    return { roles, contractReady: true };
}

/** Drop stale persisted parish ids; seed when empty. */
function reconcileSelectedParish(state: UserState) {
    const staffParishIds = new Set(state.staffRoles.map((r) => r.parishId));
    const membershipIds = new Set(state.myParishes.map((p) => p.parishId));
    const validIds = new Set([...staffParishIds, ...membershipIds]);

    if (state.selectedParishId && validIds.size > 0 && !validIds.has(state.selectedParishId)) {
        state.selectedParishId = null;
        state.selectedParishName = null;
    }

    if (!state.selectedParishId) {
        if (state.myParishes.length) {
            const preferred =
                state.myParishes.find((p) => p.isDefault && staffParishIds.has(p.parishId)) ||
                state.myParishes.find((p) => staffParishIds.has(p.parishId)) ||
                state.myParishes.find((p) => p.isDefault) ||
                state.myParishes[0];
            state.selectedParishId = preferred.parishId;
            state.selectedParishName = preferred.name;
        } else if (state.staffRoles.length > 0) {
            state.selectedParishId = state.staffRoles[0].parishId;
            state.selectedParishName = null;
        }
    } else {
        const match = state.myParishes.find((p) => p.parishId === state.selectedParishId);
        if (match?.name) state.selectedParishName = match.name;
    }
}

/** Map GET/PATCH /me profile fields into user slice display shape. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
function applyProfileToState(state: UserState, profile: any) {
    if (!profile) return;
    const addresses = profile.addresses || [];
    if (addresses.length) {
        const addressId = state?.address?.id || addresses[0]?.id;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
        state.address = addresses.find((ad: any) => addressId === ad.id) ?? addresses[0];
    } else if (Array.isArray(profile.addresses)) {
        state.address = null;
    }
    state.userInfo = {
        ...profile,
        name: `${profile.firstName || ''} ${profile.lastName || ''}`.trim(),
        username: profile.email,
        telephone: profile.phone,
        imgUrl: profile.avatarUrl,
        addresses,
    };
    state.userResponse = profile;
}

/** Build PATCH /v1/me body from account edit form (street1/zipCode contract). */
export function toUpdateMePayload(form: {
    firstName?: string;
    lastName?: string;
    nickname?: string | null;
    address?: {
        address1?: string;
        street1?: string;
        address2?: string;
        street2?: string;
        city?: string;
        state?: string;
        zip?: string;
        zipCode?: string;
        country?: string;
        isDefault?: boolean;
    };
}): UpdateMePayload {
    const addr = form.address || {};
    const street1 = (addr.street1 || addr.address1 || '').trim();
    const city = (addr.city || '').trim();
    const region = (addr.state || '').trim();
    const zipCode = (addr.zipCode || addr.zip || '').trim();
    const countryRaw = (addr.country || 'US').trim().toUpperCase();
    const country = countryRaw.length === 2 ? countryRaw : 'US';

    const payload: UpdateMePayload = {};
    if (form.firstName?.trim()) payload.firstName = form.firstName.trim();
    if (form.lastName?.trim()) payload.lastName = form.lastName.trim();
    if (form.nickname !== undefined) payload.nickname = form.nickname;

    if (street1 && city && region && zipCode) {
        const street2 = (addr.street2 || addr.address2 || '').trim();
        payload.addresses = [
            {
                street1,
                ...(street2 ? { street2 } : {}),
                city,
                state: region,
                zipCode,
                country,
                isDefault: addr.isDefault !== false,
            },
        ];
    }
    return payload;
}

/** PUT /v1/me/avatar — multipart `file` field. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const setMyPicture = createAsyncThunkWrapper<any, { file: Blob }>('user/setMyPicture', async ({ file }, { dispatch }) => {
    const formData = buildFileFormData(file);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const res: any = await appClient.put('me/avatar', formData);
    notify('Picture updated successfully!', 'success');
    // Refresh full /me (incl. staffRoles); avatar URL is short-lived presigned.
    await dispatch(myUserInfo());
    return res;
});

/** DELETE /v1/me/avatar */
export const removeMyPicture = createAsyncThunkWrapper('user/removeMyPicture', async (_, { dispatch }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const res: any = await appClient.delete('me/avatar');
    notify('Picture removed successfully!', 'success');
    await dispatch(myUserInfo());
    return res;
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const fetchUserInfo = () => async (dispatch: any) => {
    const result = await dispatch(myUserInfo());
    // Only force logout on a genuine auth failure (401). Any other failure (network,
    // CORS, etc.) shouldn't kill a valid session — myUserInfo resolves with null then.
    if (result?.error) {
        dispatch(logoutUser());
    }
};

/**
 * Refetch GET /v1/me after staff-role mutations (assign/remove/invite accept)
 * so Header / Drawer / RequireMerchant see updated staffRoles.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const refreshMeStaffRoles = () => async (dispatch: any) => {
    await dispatch(myUserInfo());
};

/**
 * A user provisioned as staff with no ParishMembership never gets a name from
 * /me or /me/parishes (reconcileSelectedParish leaves selectedParishName null for
 * that case) — fall back to GET /v1/parishes/:id, the same lookup Parishes &
 * Organizations already uses, and cache the result on the user slice.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches this slice's other untyped thunk-creator signatures
export const resolveSelectedParishName = () => async (dispatch: any, getState: any) => {
    const { selectedParishId, selectedParishName } = getState().user;
    if (!selectedParishId || selectedParishName) return;
    // Call the endpoint directly rather than dispatching fetchParishDetails: that thunk has no
    // silent option and its failures toast (correct for its own user-initiated caller, Parishes
    // & Organizations) — this is a background hydration nicety on every login, so a transient
    // failure should just leave the name blank, not surface an unrelated error toast.
    try {
        const response = await appClient.get(`parishes/${selectedParishId}`);
        const parish = normalizeParish(response as ParishPublic);
        if (parish.name) {
            dispatch(setSelectedParish({ parishId: selectedParishId, name: parish.name }));
        }
    } catch {
        // Best-effort only — leave selectedParishName null; nothing else to do here.
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const myUserInfo = createAsyncThunkWrapper<any | null>(
    'user/userInfo',
    async () => {
        try {
            // GET /v1/me — profile + staffRoles[] (approved Phase 0 contract).
            const response = await appClient.get('me');
            return response;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
        } catch (error: any) {
            if (error?.response?.status === 401) {
                throw error;
            }
            return null;
        }
    },
    { errorMessage: 'Error while fetching user info, Please contact support' }
);

/** GET /v1/me/parishes — hydrate selected parish when none is set. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const fetchMyParishes = createAsyncThunkWrapper<any | null>(
    'user/myParishes',
    async () => {
        try {
            const response = await appClient.get('me/parishes');
            return response;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
        } catch (error: any) {
            if (error?.response?.status === 401) {
                throw error;
            }
            return null;
        }
    },
    { errorMessage: 'Error while fetching parishes' }
);

/** PATCH /v1/me — profile fields supported by the new backend (no phone; no legacy envelope). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const userUpdateInfo = createAsyncThunkWrapper<any, UpdateMePayload>('user/userUpdateInfo', async (payload, { dispatch }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const response: any = await appClient.patch('me', payload);
    notify('Information updated successfully!', 'success');
    await dispatch(myUserInfo());
    return response;
});

export type LogoutOptions = { silent?: boolean };

export const logoutUser = createAsyncThunkWrapper<unknown, LogoutOptions | undefined>(
    'user/logoutUser',
    async (opts, { dispatch, getState }) => {
        // Do not remove this try catch block as we need to logout the user even if logout api fails
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
            const state: any = getState();
            const refreshToken = state?.auth?.data?.refreshToken;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
            const res: any = refreshToken ? await authClient.post('auth/logout', { refreshToken }, { hideToast: true }) : null;
            return res;
        } catch (e) {
            return e;
        } finally {
            authClient.clearTokens();
            dispatch(resetState());
            if (!opts?.silent) {
                notify('Logged out successfully!', 'success');
            }
        }
    }
);

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setSelectedParish(state, action: PayloadAction<{ parishId: string; name?: string | null }>) {
            state.selectedParishId = action.payload.parishId;
            if (action.payload.name !== undefined) {
                state.selectedParishName = action.payload.name;
            }
        },
        clearParishContext(state) {
            state.selectedParishId = null;
            state.selectedParishName = null;
            state.staffRoles = [];
            state.staffRolesContractReady = false;
            state.mustChangePassword = false;
            state.myParishes = [];
            state.meHydrated = false;
            state.myParishesHydrated = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(myUserInfo.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(myUserInfo.fulfilled, (state, action) => {
            state.loading = false;
            const profile = action.payload;
            if (!profile) {
                // Soft failure (network/5xx): keep prior roles/session; do not
                // mark ready with an empty staff set (that falsely denies portal).
                return;
            }

            state.meHydrated = true;
            // Missing staffRoles → treat as [] so shell hydration can complete.
            const { roles, contractReady } = normalizeStaffRoles(Array.isArray(profile.staffRoles) ? profile.staffRoles : []);
            state.staffRoles = roles;
            state.staffRolesContractReady = contractReady;
            state.mustChangePassword = profile.mustChangePassword === true;

            reconcileSelectedParish(state);

            applyProfileToState(state, profile);
        });
        builder.addCase(myUserInfo.rejected, (state, action) => {
            state.loading = false;
            // 401 path: fetchUserInfo logs out. Do not mark contract ready with [].
            state.error = action.error.message || null;
        });

        builder.addCase(fetchMyParishes.pending, (state) => {
            state.myParishesLoading = true;
        });
        builder.addCase(fetchMyParishes.fulfilled, (state, action) => {
            state.myParishesLoading = false;
            const payload = action.payload;
            if (payload == null) {
                // Soft failure: keep prior myParishes; still mark hydrated so the
                // shell can proceed (portal gate uses staffRoles from /me, not this).
                state.myParishesHydrated = true;
                return;
            }
            state.myParishesHydrated = true;
            const items = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];
            state.myParishes = items
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
                .map((item: any) => {
                    const parish = item.parish || item;
                    const parishId = String(parish.id || item.parishId || '');
                    return {
                        id: String(item.id || parishId),
                        parishId,
                        name: parish.name ?? parish.displayName ?? null,
                        isDefault: Boolean(item.isDefault),
                    };
                })
                .filter((p: { parishId: string }) => p.parishId);

            reconcileSelectedParish(state);
        });
        builder.addCase(fetchMyParishes.rejected, (state) => {
            state.myParishesLoading = false;
            // Keep prior myParishes; mark hydrated so RequireMerchant is not stuck.
            state.myParishesHydrated = true;
        });

        builder.addCase(userUpdateInfo.pending, (state) => {
            state.updateInfoLoading = true;
        });
        builder.addCase(userUpdateInfo.fulfilled, (state) => {
            state.updateInfoLoading = false;
        });
        builder.addCase(userUpdateInfo.rejected, (state, action) => {
            state.updateInfoLoading = false;
            state.updateInfoError = action.error.message || null;
        });
        builder.addCase(logoutUser.pending, (state) => {
            state.logoutLoading = true;
        });
        builder.addCase(logoutUser.fulfilled, (state) => {
            state.logoutLoading = false;
        });
        builder.addCase(logoutUser.rejected, (state) => {
            state.logoutLoading = false;
        });
    },
});

export const { setSelectedParish, clearParishContext } = userSlice.actions;
export default userSlice.reducer;
